import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface SseDetail {
  status: string;
  doctor_id?: string;
  doctor_name?: string;
  logged_at?: string;
  doctor_index?: number;
  total_doctors?: number;
  room_id?: string;
  call_duration_seconds?: number;
  failed_doctors?: { doctor_id: string; reason: string; doctor_name?: string }[];
  accepted_at?: string;
}

interface ConsultationState {
  ticketId: string | null;
  consultationId: string | null;
  sseDetail: SseDetail | null;
  acceptedDetail: { doctor_id: string; accepted_at: string; doctor_name?: string; doctor_phone?: string } | null;
  eventLog: SseDetail[];
}

const initialState: ConsultationState = {
  ticketId: null,
  consultationId: null,
  sseDetail: null,
  acceptedDetail: null,
  eventLog: [],
};

const consultationSlice = createSlice({
  name: "consultation",
  initialState,
  reducers: {
    setConsultationId: (state, action: PayloadAction<{ ticketId: string; consultationId: string }>) => {
      if (state.consultationId !== action.payload.consultationId) {
        state.sseDetail = null;
        state.acceptedDetail = null;
        state.eventLog = [];
      }
      state.ticketId = action.payload.ticketId;
      state.consultationId = action.payload.consultationId;
    },
    setSseDetail: (state, action: PayloadAction<SseDetail>) => {
      state.sseDetail = action.payload;
      if (action.payload.status === "accepted" && action.payload.doctor_id && action.payload.accepted_at) {
        state.acceptedDetail = {
          doctor_id: action.payload.doctor_id,
          accepted_at: action.payload.accepted_at,
        };
      }
    },
    setAcceptedDoctorInfo: (state, action: PayloadAction<{ doctor_name: string; doctor_phone: string }>) => {
      if (state.acceptedDetail) {
        state.acceptedDetail.doctor_name = action.payload.doctor_name;
        state.acceptedDetail.doctor_phone = action.payload.doctor_phone;
      }
    },
    appendEventLog: (state, action: PayloadAction<SseDetail>) => {
      state.eventLog.push(action.payload);
    },
    resetConsultation: (state) => {
      state.ticketId = null;
      state.consultationId = null;
      state.sseDetail = null;
      state.acceptedDetail = null;
      state.eventLog = [];
    },
    /**
     * Atomically sets the entire consultation state from a backend response.
     * Used when restoring from the database on navigation/page-reload so the
     * SSE useEffect sees the correct terminal status immediately.
     */
    hydrateConsultation: (
      state,
      action: PayloadAction<{
        ticketId: string;
        consultationId: string;
        sseDetail: SseDetail;
        acceptedDetail: {
          doctor_id: string;
          accepted_at: string;
          doctor_name?: string;
          doctor_phone?: string;
        } | null;
        eventLog: SseDetail[];
      }>,
    ) => {
      state.ticketId = action.payload.ticketId;
      state.consultationId = action.payload.consultationId;
      state.sseDetail = action.payload.sseDetail;
      state.acceptedDetail = action.payload.acceptedDetail;
      state.eventLog = action.payload.eventLog;
    },
  },
});

export const { setConsultationId, setSseDetail, setAcceptedDoctorInfo, appendEventLog, resetConsultation, hydrateConsultation } =
  consultationSlice.actions;

export default consultationSlice.reducer;
